<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FormInput, ThemeProvider, UiModalHeader } from '@/shared/ui';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

  const props = withDefaults(
    defineProps<{
      initialTitle: string;
      placeholder: string;
      maxLength: number;
      isSaving?: boolean;
      conflictMessage?: string;
    }>(),
    {
      isSaving: false,
      conflictMessage: '',
    }
  );

  const emit = defineEmits<{
    close: [];
    save: [title: string];
  }>();

  const FIELD_ID = 'rename-title';
  // The focus trap would focus the close button and select the whole title; with a delayed initial
  // focus the selection lands after onPostActivate.
  const FOCUS_TRAP = {
    allowOutsideClick: true,
    initialFocus: `#${FIELD_ID}`,
    delayInitialFocus: false,
    onPostActivate: () => {
      const field = fieldWrapper.value?.querySelector('input');
      if (field) {
        field.setSelectionRange(field.value.length, field.value.length);
      }
    },
  };

  const fieldWrapper = ref<HTMLElement | null>(null);
  const title = ref(props.initialTitle);
  const isConflictDismissed = ref(false);

  const trimmedTitle = computed(() => title.value.trim());
  // The field's maxlength stops typing but leaves a longer title created elsewhere untouched.
  const isTooLong = computed(() => trimmedTitle.value.length > props.maxLength);
  const canSave = computed(
    () => trimmedTitle.value.length > 0 && !isTooLong.value && !props.isSaving
  );
  const errorMessage = computed(() => {
    if (isTooLong.value) return `O título deve ter no máximo ${props.maxLength} caracteres.`;
    return isConflictDismissed.value ? undefined : props.conflictMessage || undefined;
  });

  watch(
    () => props.conflictMessage,
    (message) => {
      if (message) isConflictDismissed.value = false;
    }
  );

  const updateTitle = (value: string) => {
    title.value = value;
    isConflictDismissed.value = true;
  };

  const handleSave = () => {
    if (!canSave.value) return;
    emit('save', trimmedTitle.value);
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-full max-w-md shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :focus-trap="FOCUS_TRAP"
  >
    <ThemeProvider class="flex flex-col">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
        title="Renomear"
        @close="emit('close')"
      />

      <div
        ref="fieldWrapper"
        class="px-6 py-6 flex flex-col gap-1"
      >
        <FormInput
          :id="FIELD_ID"
          :model-value="title"
          label="Título"
          type="text"
          :placeholder="placeholder"
          :max-length="maxLength"
          label-class="text-sm text-gray-07 font-semibold"
          :error="errorMessage"
          @update:model-value="updateTitle"
        />

        <p
          data-testid="rename-title-counter"
          class="self-end text-xs text-gray-03"
        >
          {{ title.length }}/{{ maxLength }}
        </p>
      </div>

      <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isSaving"
          @click="emit('close')"
        />
        <ColoredButton
          text="Salvar"
          :disabled="!canSave"
          :loading="isSaving"
          :icon="CheckMarkIcon"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
